import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useToast } from "@shared/ui/use-toast";
import { queryClient } from "@shared/api/queries/queryClient";
import {
  useAssignConsumer,
  useUpdateMeterDispatchDetail,
} from "@features/mx/meterReads/hooks";

interface Reader {
  id: number;
  employee_id: string;
  name: string;
  email: string;
  assigned_routes: number;
  contact_number: string;
  ratings: number;
  availability: string;
}

interface UpdateMeterDispatchPayload {
  is_assigned: boolean;
  reader_id: string[];
}

interface AssignReaderModalProps {
  assignOpen: boolean;
  unassignOpen: boolean;
  onAssignClose: () => void;
  onUnassignClose: () => void;
  selectedDispatchId: number | null;
  selectedRouteInfo: { route: string; assignedTo: string } | null;
  initialReaderIds?: string[];
  refetch: () => void;
}

export const AssignReaderModal: React.FC<AssignReaderModalProps> = ({
  assignOpen,
  unassignOpen,
  onAssignClose,
  onUnassignClose,
  selectedDispatchId,
  selectedRouteInfo,
  initialReaderIds = [],
  refetch,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const { toast } = useToast();

  const [selectedReaderIds, setSelectedReaderIds] = useState<string[]>([]);
  const [protectedReaderIds, setProtectedReaderIds] = useState<string[]>([]);
  const [allReaderIds, setAllReaderIds] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [assignPage, setAssignPage] = useState(1);

  useEffect(() => {
    if (assignOpen) {
      setSelectedReaderIds(initialReaderIds);
      setProtectedReaderIds(initialReaderIds);
    } else {
      setSelectedReaderIds([]);
      setProtectedReaderIds([]);
      setSearchText("");
    }
  }, [assignOpen, initialReaderIds]);

  const { data: meterAssignData } = useAssignConsumer({
    remote_utility_id: remoteUtilityId,
    isActive: "True",
    page: assignPage,
    search: searchText,
  });

  const { mutate: updateMeterDispatch, isPending: isUpdating } = useUpdateMeterDispatchDetail();

  useEffect(() => {
    if (meterAssignData?.results) {
      const readerIds = meterAssignData.results.map((reader: any) => reader.id);
      setAllReaderIds(readerIds);
    }
  }, [meterAssignData]);

  const filteredReaders = useMemo(() => {
    if (!meterAssignData?.results) return [];

    const readers: Reader[] = meterAssignData.results.map((reader: any) => ({
      id: reader.id,
      employee_id: reader.employeeId,
      name: reader.name,
      email: reader.email,
      assigned_routes: reader.assignedRoutes,
      contact_number: reader.contactNumber,
      ratings: reader.ratings,
      availability: reader.availability,
    }));

    if (!searchText.trim()) return readers;

    return readers.filter(
      (reader) =>
        reader.name.toLowerCase().includes(searchText.toLowerCase()) ||
        reader.employee_id.toLowerCase().includes(searchText.toLowerCase()) ||
        reader.email.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [meterAssignData, searchText]);

  const handleAssignSubmit = () => {
    if (selectedDispatchId && selectedReaderIds.length > 0) {
      const payload: UpdateMeterDispatchPayload = {
        is_assigned: true,
        reader_id: selectedReaderIds,
      };

      updateMeterDispatch(
        {
          dispatchId: selectedDispatchId.toString(),
          payload,
        },
        {
          onSuccess: () => {
            toast({
              title: "Assignment Successful",
              description: "Reader assigned successfully to the route.",
              variant: "default",
            });
            onAssignClose();
            queryClient.refetchQueries({
              queryKey: ["meter", "meterRun"],
            });
            refetch();
          },
          onError: (err: any) => {
            toast({
              title: "Assignment Failed",
              description: err?.message || "Something went wrong.",
              variant: "destructive",
            });
          },
        },
      );
    }
  };

  const handleUnassignSubmit = () => {
    if (selectedDispatchId && allReaderIds.length > 0) {
      const payload: UpdateMeterDispatchPayload = {
        is_assigned: false,
        reader_id: allReaderIds,
      };

      updateMeterDispatch(
        {
          dispatchId: selectedDispatchId.toString(),
          payload,
        },
        {
          onSuccess: () => {
            toast({
              title: "Unassignment Successful",
              description: "Reader unassigned successfully from the route.",
              variant: "default",
            });
            onUnassignClose();
            queryClient.refetchQueries({
              queryKey: ["meter", "meterRun"],
            });
            refetch();
          },
          onError: (err: any) => {
            toast({
              title: "Unassignment Failed",
              description: err?.message || "Something went wrong.",
              variant: "destructive",
            });
          },
        },
      );
    }
  };

  const readerSelectColumns: TableColumn<Reader>[] = [
    { key: "employee_id", header: "Emp Id" },
    { key: "name", header: "Name" },
    { key: "email", header: "Contact" },
    { key: "assigned_routes", header: "Workload" },
  ];

  return (
    <>
      {/* Assignment Modal */}
      <Dialog open={assignOpen} onOpenChange={onAssignClose}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-scroll">
          <DialogHeader>
            <DialogTitle>Assign Meter Reader to {selectedRouteInfo?.route}</DialogTitle>
            <DialogDescription>
              Select one or more readers to assign to this route.
              {protectedReaderIds.length > 0 && (
                <span className="block mt-2 text-blue-600 font-medium">
                  Note: Already assigned readers cannot be unchecked. Use 'Unassign' action to
                  remove them.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="mb-4">
            <GenericTable
              data={filteredReaders}
              columns={readerSelectColumns}
              rowKey="id"
              loading={!meterAssignData}
              pagination={{
                currentPage: assignPage,
                itemsPerPage: PAGE_SIZE,
                totalItems: meterAssignData?.count || filteredReaders.length,
                totalPages: Math.ceil(
                  (meterAssignData?.count || filteredReaders.length) / PAGE_SIZE,
                ),
                startItem: (assignPage - 1) * PAGE_SIZE + 1,
                endItem: Math.min(assignPage * PAGE_SIZE, filteredReaders.length),
              }}
              onPageChange={setAssignPage}
              emptyMessage={
                searchText
                  ? "No readers found matching your search"
                  : "No readers available"
              }
              selectable={true}
              selectedRows={selectedReaderIds}
              onSelectionChange={(selectedIds) => {
                const allIds = [...new Set([...protectedReaderIds, ...selectedIds])];
                setSelectedReaderIds(allIds);
              }}
              onSearch={(value) => setSearchText(value)}
              searchPlaceholder="Search meter reader by name or employee id..."
            />
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={onAssignClose} disabled={isUpdating}>
              Cancel
            </Button>
            <Button
              disabled={selectedReaderIds.length === 0 || isUpdating}
              onClick={handleAssignSubmit}
            >
              {isUpdating ? "Assigning..." : `Assign Selected (${selectedReaderIds.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unassign Confirmation Modal */}
      <Dialog open={unassignOpen} onOpenChange={onUnassignClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Unassign Route</DialogTitle>
            <DialogDescription>Confirm that you want to unassign this route.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-gray-600">
              Are you sure you want to unassign <strong>{selectedRouteInfo?.assignedTo}</strong>{" "}
              from <strong>{selectedRouteInfo?.route}</strong>?
            </p>
            <p className="text-sm text-gray-500">This will change the status to 'Unassigned'</p>
            {allReaderIds.length > 0 && (
              <p className="text-xs text-blue-600">Will unassign reader(s)</p>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={onUnassignClose} disabled={isUpdating}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleUnassignSubmit} disabled={isUpdating}>
              {isUpdating ? "Unassigning..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
